// pages/GuestListDetail.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiArrowLeft, 
  FiUserPlus, 
  FiDownload, 
  FiTrash2,
  FiUser,
  FiAtSign
} from 'react-icons/fi';
import DashboardLayout from '../components/DashboardLayout';
import AddPlatformUsersModal from '../components/AddPlatformUsersModal';
import supabase from '../services/supabase';
import '../styles/GuestLists.scss';

const GuestListDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [guestList, setGuestList] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [showAddUsersModal, setShowAddUsersModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  const LIMIT = 20;

  const loadGuestList = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('guest_lists')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setGuestList(data);
    } catch (error) {
      console.error('Error loading guest list:', error);
      alert('Failed to load guest list');
      navigate('/guest-lists');
    }
  }, [id, navigate]);

  const loadContacts = useCallback(async (pageNum = 0) => {
    try {
      if (pageNum === 0) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const from = pageNum * LIMIT;
      const to = from + LIMIT - 1;

      const { data, error, count } = await supabase
        .from('guest_list_contacts')
        .select(`
          *,
          user:users(id, first_name, last_name, username, profile_photo_cdn_url)
        `, { count: 'exact' })
        .eq('guest_list_id', id)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      if (pageNum === 0) {
        setContacts(data);
      } else {
        // From the latest list, not the one this function closed over.
        setContacts((prev) => [...prev, ...data]);
      }

      setTotal(count);
      setHasMore(to < count - 1);
      setPage(pageNum);
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [id]);

  useEffect(() => {
    loadGuestList();
    loadContacts();
  }, [id, loadGuestList, loadContacts]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      loadContacts(page + 1);
    }
  };

  const handleRemoveContact = async (contactId, contactName) => {
    if (!window.confirm(`Remove ${contactName} from this guest list?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('guest_list_contacts')
        .delete()
        .eq('id', contactId);

      if (error) throw error;

      setContacts(contacts.filter(c => c.id !== contactId));
      setTotal(total - 1);
    } catch (error) {
      console.error('Error removing contact:', error);
      alert('Failed to remove contact');
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);

      // Get all contacts (not paginated for export)
      const { data, error } = await supabase
        .from('guest_list_contacts')
        .select('*')
        .eq('guest_list_id', id);

      if (error) throw error;

      // Create CSV
      let csv = 'Name,Phone Number,Platform User\n';
      data.forEach(contact => {
        const name = contact.name.replace(/"/g, '""');
        const phone = contact.phone_number || '';
        const platformUser = contact.user_id ? 'Yes' : 'No';
        csv += `"${name}","${phone}","${platformUser}"\n`;
      });

      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${guestList.name.replace(/[^a-z0-9]/gi, '_')}_guests.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export guest list');
    } finally {
      setExporting(false);
    }
  };

  const handleUsersAdded = () => {
    loadContacts(0);
    setShowAddUsersModal(false);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading guest list...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="guest-list-detail-page">
        <div className="guest-list-detail-container">
          {/* Header */}
          <motion.div 
            className="page-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <button className="back-btn" onClick={() => navigate('/guest-lists')}>
              <FiArrowLeft size={20} />
              <span>Back to Guest Lists</span>
            </button>
          </motion.div>

          {/* Guest List Info */}
          <motion.div 
            className="guest-list-info glass-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="info-content">
              <h1>{guestList?.name}</h1>
              {guestList?.description && (
                <p className="description">{guestList.description}</p>
              )}
              <p className="contact-count">
                {total} {total === 1 ? 'contact' : 'contacts'}
              </p>
            </div>

            <div className="info-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowAddUsersModal(true)}
              >
                <FiUserPlus />
                <span>Add Users</span>
              </button>

              {total > 0 && (
                <button 
                  className="btn btn-secondary"
                  onClick={handleExportCSV}
                  disabled={exporting}
                >
                  {exporting ? (
                    <div className="spinner small"></div>
                  ) : (
                    <>
                      <FiDownload />
                      <span>Export CSV</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </motion.div>

          {/* Contacts List */}
          {contacts.length === 0 ? (
            <motion.div 
              className="empty-state glass-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <FiUserPlus size={64} />
              <h2>No Contacts Yet</h2>
              <p>Add platform users to this guest list</p>
              <button 
                className="btn btn-primary"
                onClick={() => setShowAddUsersModal(true)}
              >
                <FiUserPlus />
                <span>Add Users</span>
              </button>
            </motion.div>
          ) : (
            <motion.div 
              className="contacts-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="contacts-list">
                {contacts.map((contact, index) => (
                  <motion.div
                    key={contact.id}
                    className="contact-card glass-card"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="contact-info">
                      {contact.user ? (
                        <div className="contact-avatar">
                          {contact.user.profile_photo_cdn_url ? (
                            <img 
                              src={contact.user.profile_photo_cdn_url} 
                              alt={contact.name}
                            />
                          ) : (
                            <div className="avatar-placeholder">
                              {contact.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="platform-badge">
                            <FiUser size={12} />
                          </div>
                        </div>
                      ) : (
                        <div className="contact-avatar">
                          <div className="avatar-placeholder">
                            {contact.name.charAt(0).toUpperCase()}
                          </div>
                        </div>
                      )}

                      <div className="contact-details">
                        <h4>{contact.name}</h4>
                        {contact.user ? (
                          <div className="username">
                            <FiAtSign size={14} />
                            <span>{contact.user.username}</span>
                          </div>
                        ) : contact.phone_number && (
                          <p className="phone">{contact.phone_number}</p>
                        )}
                      </div>
                    </div>

                    <div className="contact-actions">
                      {contact.user_id && (
                        <button
                          className="icon-btn"
                          onClick={() => navigate(`/${contact.user.username}`)}
                        >
                          <FiUser />
                        </button>
                      )}

                      <button
                        className="icon-btn danger"
                        onClick={() => handleRemoveContact(contact.id, contact.name)}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>

              {hasMore && (
                <button
                  className="btn btn-load-more"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <div className="spinner small"></div>
                  ) : (
                    <span>Load More</span>
                  )}
                </button>
              )}
            </motion.div>
          )}
        </div>
      </div>

      {/* Add Users Modal */}
      <AddPlatformUsersModal
        isOpen={showAddUsersModal}
        onClose={() => setShowAddUsersModal(false)}
        guestListId={id}
        onUsersAdded={handleUsersAdded}
        existingContactIds={contacts.filter(c => c.user_id).map(c => c.user_id)}
      />
    </DashboardLayout>
  );
};

export default GuestListDetail;